import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useContext,
  useRef,
  useState,
} from 'react'
import type { Api } from '@reduxjs/toolkit/query/react'
import type { AgentFullConfig } from '../hook/useGetAgentConfig'
import type { AgentWidgetMode } from '../types/agent-mode'
import { isFullscreenWidgetMode } from '../types/agent-mode'
import type { AgentCloseEvent, AgentOutputEvent, LibConfig } from '../types/lib-config'

type AnyApi = Api<any, any, any, any, any>

interface WidgetContextState {
  isWidgetOpen: boolean
  isAIOpen: boolean
  isModeFullscreen: boolean
  isDocActive: boolean
  isApiActive: boolean
  isChatActive: boolean
  mode: AgentWidgetMode
  config: LibConfig
  hideChat: () => void
  toggle: (type: ToggleTypes, isActive?: boolean) => void
  emitOutput: (event: AgentOutputEvent) => void
  queuePrompt: (prompt: string) => void
  consumeQueuedPrompt: () => string | null
  queuedPromptVersion: number
  agent: AgentFullConfig
  agentApi: AnyApi
  chatApi: AnyApi
  modeApi: AnyApi
}

export const WidgetContext = createContext<WidgetContextState | undefined>(
  undefined
)

export const useWidgetContext = () => {
  const widgetContext = useContext(WidgetContext)

  if (!widgetContext)
    throw new Error(
      'No widgetContext.Provider found when calling useWidgetContext.'
    )

  return widgetContext
}

export enum ToggleTypes {
  doc = 'doc',
  api = 'api',
  chat = 'chat',
  ai = 'ai',
  widget = 'widget',
}

export const WidgetContextProvider = ({
  children,
  agent,
  agentApi,
  chatApi,
  modeApi,
  mode,
  config,
}: {
  children: ReactNode
  agent: AgentFullConfig
  agentApi: AnyApi
  chatApi: AnyApi
  modeApi: AnyApi
  mode: AgentWidgetMode
  config: LibConfig
}) => {
  const [isAIOpen, setIsAIOpen] = useState(false)
  const [isModeFullscreen, setIsModeFullscreen] = useState(false)
  const [isWidgetOpen, setIsWidgetOpen] = useState(false)
  const [isDocActive, setIsDocActive] = useState(false)
  const [isApiActive, setIsApiActive] = useState(false)
  const [isChatActive, setIsChatActive] = useState(true)
  const [queuedPromptVersion, setQueuedPromptVersion] = useState(0)
  const queuedPromptRef = useRef<string | null>(null)
  const instanceId =
    typeof (config as any)?.instanceId === 'string'
      ? String((config as any).instanceId).trim() || undefined
      : undefined

  const hideChat = () => {
    setIsChatActive(false)
  }

  const showChat = () => {
    setIsChatActive(true)
  }

  const hideAllFrames = () => {
    setIsApiActive(false)
    setIsDocActive(false)
    setIsChatActive(false)
    setIsAIOpen(false)
    setIsModeFullscreen(false)
  }

  const queuePrompt = useCallback((prompt: string) => {
    const next = prompt.trim()
    if (!next) return
    queuedPromptRef.current = next
    setQueuedPromptVersion((prev) => prev + 1)
  }, [])

  useEffect(() => {
    const open = (event: Event) => {
      const detail = (event as CustomEvent).detail
      if (!detail || detail.instanceId !== instanceId) return
      detail.accepted = true
      setIsWidgetOpen(true)
      setIsAIOpen(true)
      setIsChatActive(true)
      if (typeof detail.prompt === 'string') queuePrompt(detail.prompt)
    }
    window.addEventListener('osirus:open-chat', open)
    return () => window.removeEventListener('osirus:open-chat', open)
  }, [instanceId, queuePrompt])

  const consumeQueuedPrompt = useCallback(() => {
    if (!queuedPromptRef.current) return null
    const next = queuedPromptRef.current
    queuedPromptRef.current = null
    return next
  }, [])

  const emitOutput = useCallback(
    (event: AgentOutputEvent) => {
      const handler = (config as any)?.onOutput
      if (typeof handler !== 'function') return

      const payload: AgentOutputEvent = {
        ...event,
        mode: event.mode ?? mode,
        agentId: event.agentId ?? agent?.id ?? undefined,
        instanceId: event.instanceId ?? instanceId,
        timestamp: event.timestamp ?? new Date().toISOString(),
      }

      try {
        handler(payload)
      } catch {
        // Ignore host callback failures to avoid breaking widget UX
      }
    },
    [agent?.id, config, instanceId, mode]
  )

  const emitClose = useCallback(
    (reason: string) => {
      const handler = (config as any)?.onClose
      if (typeof handler !== 'function') return

      const payload: AgentCloseEvent = {
        reason: reason || 'close',
        mode,
        agentId: agent?.id ?? undefined,
        instanceId,
        timestamp: new Date().toISOString(),
      }

      try {
        handler(payload)
      } catch {
        // Ignore host callback failures to avoid breaking widget UX
      }
    },
    [agent?.id, config, instanceId, mode]
  )

  const toggle = (type: ToggleTypes, isActive?: boolean) => {
    switch (type) {
      case ToggleTypes.chat:
        isActive ? hideChat() : showChat()
        break
      case ToggleTypes.api:
      case ToggleTypes.doc:
        hideAllFrames()

        if (isActive) {
          break
        }

        if (type === ToggleTypes.api) {
          setIsApiActive(true)
          break
        }

        if (type === ToggleTypes.doc) {
          setIsDocActive(true)
        }

        break
      case ToggleTypes.widget:
        if (typeof isActive === 'boolean') {
          setIsWidgetOpen((prev) => {
            if (prev && !isActive) emitClose('widget-close')
            return isActive
          })
        } else {
          setIsWidgetOpen((prev) => {
            const next = !prev
            if (prev && !next) emitClose('widget-close')
            return next
          })
        }
        break
      case ToggleTypes.ai:
        hideAllFrames()

        if (isActive) {
          break
        }

        setIsAIOpen((prev) => (isFullscreenWidgetMode(mode) ? true : !prev))
        setIsModeFullscreen(isFullscreenWidgetMode(mode))
        break
      default:
        throw new Error(`Type "${type}" is not valid`)
    }
  }

  return (
    <WidgetContext.Provider
      value={{
        isWidgetOpen,
        isDocActive,
        isApiActive,
        isChatActive,
        isAIOpen,
        isModeFullscreen,
        mode,
        config,
        toggle,
        hideChat,
        emitOutput,
        queuePrompt,
        consumeQueuedPrompt,
        queuedPromptVersion,
        agent,
        agentApi,
        chatApi,
        modeApi,
      }}
    >
      {children}
    </WidgetContext.Provider>
  )
}
